/**
 * The Projects page: two columns.
 *
 * Left, the projects (folders) with the Unfiled bucket pinned on top; each
 * row is a drop target. Right, the items of the open project in three
 * sections (explorations, selections, reproductions), as the very cards the
 * module overview lists show. A card is dragged onto a project row to file
 * it there; Ctrl-click picks several cards for one drag; the folder button
 * in a card's header does the same without a mouse drag. Each section's
 * "New ..." button creates an item of that kind inside the open project.
 *
 * The open project is in the URL (/projects/<id>, /projects/unfiled); the
 * Mine/All toggle narrows the items like the overview lists.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, LinearProgress, Menu, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import axios from "axios";
import ProjectList, { countsOf } from "../../components/ProjectList";
import ExplorationCard from "../../components/ExplorationCard";
import SelectionCard from "../../components/SelectionCard";
import ReproductionCard from "../../components/ReproductionCard";
import useProjects from "../../components/useProjects";
import useReproductionThumbnails from "../../components/useReproductionThumbnails";
import ListScopeToggle, { useListScope } from "../../components/ListScopeToggle";
import { CARD_GRID_SX } from "../../components/entityCardChrome";
import { ITEM_KIND_STYLES } from "../../components/itemKindStyles";
import { deleteRights, useDeleteRights } from "../../helper/deleteRights";
import {
    UNFILED, projectRoute, fetchProjectItems, moveItems, createProject, updateProject,
    deleteProject, writeDragItems, withProjectParam, projectErrorMessage,
} from "../../helper/projects";
import { EXPLORATION_SETUP_ROUTE, explorationRoute, explorationResumeRoute } from "../../helper/explorationRoutes";
import { normalizeExplorationStatus } from "../../components/ExplorationCard";
import {
    SELECTION_KINDS, SELECTION_KIND_KEYS, selectionResumeRoute, selectionRoute, selectionSetupRoute,
} from "../../helper/selectionFlow";
import {
    REPRODUCTION_KINDS, REPRODUCTION_KIND_SLUGS, newReproductionRoute, reproductionRoute,
} from "../../helper/reproductionFlow";
import { downloadReproductionSource } from "../../helper/reproductionSource";

const EMPTY_ITEMS = { explorations: [], selections: [], reproductions: [] };

/** yyyy-mm-dd of a timestamp in local time (the exploration delete confirm). */
function localDateStamp(iso) {
    if (!iso) return "";
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
}

/** What deleting one item needs: the endpoint, the confirm text and its message field. */
const DELETE_RULES = {
    exploration: {
        title: "Delete exploration?",
        endpoint: (id) => `api/exploration/delete/${id}/`,
        confirm: (row) => localDateStamp(row.created_at),
        label: "Type the creation date to confirm",
        text: (row, expected) => (
            <>This permanently deletes the exploration “{row.name}” created on <strong>{expected || "—"}</strong> with
            all of its iterations and measurements. This action cannot be undone.</>
        ),
        failed: (error) => error?.response?.data?.message || "Deleting the exploration failed.",
    },
    selection: {
        title: "Delete selection?",
        endpoint: (id) => `api/selection/delete/${id}/`,
        confirm: (row) => row.name,
        label: "Type the selection name to confirm",
        text: (row) => (
            <>This permanently deletes the selection “{row.name}” with all of its filter results and marking
            files. This action cannot be undone.</>
        ),
        failed: (error) => error?.response?.data?.message || "Deleting the selection failed.",
    },
    reproduction: {
        title: "Delete reproduction?",
        endpoint: (id) => `api/reproduction/delete/${id}/`,
        confirm: (row) => row.name,
        label: "Type the reproduction name to confirm",
        text: (row) => (
            <>This permanently deletes the reproduction “{row.name}” with its color assignments and marking
            file. This action cannot be undone.</>
        ),
        failed: (error) => error?.response?.data?.error || "Deleting the reproduction failed.",
    },
};

const keyOf = (kind, id) => `${kind}:${id}`;

/**
 * One kind of item of the open project: a header band with the kind's icon,
 * its colour and the count, sticky while its cards scroll, above a tinted
 * panel with the card grid. The three bands make the three kinds read as
 * three distinct groups. `action` sits at the right end of the band (the
 * section's "New ..." button).
 */
function Section({ kind, count, action, children }) {
    const { label, singular, Icon, accent, tint } = ITEM_KIND_STYLES[kind];
    return (
        <Box component="section" aria-label={label} sx={{ mb: 4 }}>
            <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                    position: "sticky", top: 0, zIndex: 2,
                    bgcolor: "#ffffff", py: 1.25, mb: 1.5,
                    borderBottom: "1px solid rgba(15, 23, 42, 0.06)",
                }}
            >
                <Box
                    sx={{
                        width: 36, height: 36, borderRadius: 2, flexShrink: 0,
                        bgcolor: accent, color: "#ffffff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: `0 6px 14px ${accent}55`,
                    }}
                    aria-hidden="true"
                >
                    <Icon sx={{ fontSize: 20 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{label}</Typography>
                <Box
                    sx={{
                        px: 1, py: 0.25, borderRadius: 999, fontSize: 12.5, fontWeight: 700,
                        color: accent, bgcolor: `${accent}1A`, border: `1px solid ${accent}55`,
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                    }}
                >
                    {count}
                </Box>
                <Box sx={{ flex: 1, height: 2, borderRadius: 999, background: `linear-gradient(90deg, ${accent}66, ${accent}00)` }} />
                {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
            </Stack>
            <Box
                sx={{
                    borderRadius: 3, p: { xs: 1.5, sm: 2 },
                    bgcolor: tint, border: `1px solid ${accent}33`, borderLeft: `4px solid ${accent}`,
                }}
            >
                {count === 0 ? (
                    <Typography variant="body2" sx={{ color: "text.secondary", py: 1, px: 0.5 }}>
                        No {singular}s in this project.
                    </Typography>
                ) : (
                    <Box sx={CARD_GRID_SX}>{children}</Box>
                )}
            </Box>
        </Box>
    );
}

/** The section's "New ..." button in the kind's colour. */
const newButtonSx = (accent) => ({
    textTransform: "none", fontWeight: 700, borderRadius: 2, flexShrink: 0, whiteSpace: "nowrap",
    bgcolor: accent, "&:hover": { bgcolor: accent, filter: "brightness(0.9)" },
});

function NewButton({ kind, onClick, ...props }) {
    const { accent } = ITEM_KIND_STYLES[kind];
    return (
        <Button
            size="small"
            variant="contained"
            disableElevation
            startIcon={<AddIcon />}
            onClick={onClick}
            sx={newButtonSx(accent)}
            {...props}
        />
    );
}

/**
 * A "New ..." button that opens a menu of the kind's types (the type choice
 * pages do not carry the project along, so the type is chosen here and the
 * create page opens with ?project=).
 */
function NewMenuButton({ kind, label, options, onChoose }) {
    const [anchor, setAnchor] = useState(null);
    return (
        <>
            <NewButton
                kind={kind}
                endIcon={<ArrowDropDownIcon />}
                onClick={(event) => setAnchor(event.currentTarget)}
                aria-haspopup="menu"
            >
                {label}
            </NewButton>
            <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
                {options.map((option) => (
                    <MenuItem
                        key={option.key}
                        dense
                        onClick={() => { setAnchor(null); onChoose(option.route); }}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

export default function ProjectsHome() {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const activeId = projectId || null;
    const { projects, unfiled, loading: projectsLoading, reload: reloadProjects } = useProjects();
    const [scope, setScope] = useListScope();
    const me = useDeleteRights();
    const { thumbnails, load: loadThumbnails } = useReproductionThumbnails();

    const [items, setItems] = useState({ loading: false, ...EMPTY_ITEMS });
    const [message, setMessage] = useState(null); // { severity, text }
    const [selectedKeys, setSelectedKeys] = useState(() => new Set());
    // The project dialog: { mode: "create" | "rename", project?, name, description }.
    const [projectDialog, setProjectDialog] = useState(null);
    const [projectDialogError, setProjectDialogError] = useState(null);
    // Delete project: { project, input }.
    const [deleteProjectDialog, setDeleteProjectDialog] = useState(null);
    // Delete item: { kind, row, input }.
    const [deleteItemDialog, setDeleteItemDialog] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);

    const activeProject = useMemo(
        () => (activeId && activeId !== UNFILED ? projects.find((p) => p.id === activeId) || null : null),
        [projects, activeId],
    );

    // No project in the URL: open the first project, or Unfiled. A project
    // that no longer exists (deleted, stale link) falls back to Unfiled.
    useEffect(() => {
        if (projectsLoading) return;
        if (!activeId) {
            navigate(projectRoute(projects[0]?.id || UNFILED), { replace: true });
        } else if (activeId !== UNFILED && !projects.some((p) => p.id === activeId)) {
            navigate(projectRoute(UNFILED), { replace: true });
        }
    }, [projectsLoading, projects, activeId, navigate]);

    const loadItems = useCallback(async () => {
        if (!activeId) return;
        setItems((old) => ({ ...old, loading: true }));
        try {
            const data = await fetchProjectItems(activeId, scope);
            setItems({ loading: false, explorations: data.explorations, selections: data.selections, reproductions: data.reproductions });
            loadThumbnails(data.reproductions);
        } catch (error) {
            console.error("Loading the project's items failed:", error);
            setItems({ loading: false, ...EMPTY_ITEMS });
            setMessage({ severity: "error", text: "Loading the project's items failed." });
        }
    }, [activeId, scope, loadThumbnails]);

    useEffect(() => {
        setSelectedKeys(new Set());
        loadItems();
    }, [loadItems]);

    // Esc drops the multi-card pick.
    useEffect(() => {
        const onKey = (event) => { if (event.key === "Escape") setSelectedKeys(new Set()); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const rightsOf = (row, kind) => deleteRights(row, me, kind);

    // ---- moving ------------------------------------------------------------
    const moveTo = async (targetId, moved) => {
        if ((targetId || UNFILED) === activeId) return;
        const keys = new Set(moved.map((item) => keyOf(item.kind, item.id)));
        const before = items;
        // Optimistic: the cards leave the open project right away.
        setItems((old) => ({
            ...old,
            explorations: old.explorations.filter((row) => !keys.has(keyOf("exploration", row.id))),
            selections: old.selections.filter((row) => !keys.has(keyOf("selection", row.id))),
            reproductions: old.reproductions.filter((row) => !keys.has(keyOf("reproduction", row.id))),
        }));
        setSelectedKeys((old) => new Set([...old].filter((key) => !keys.has(key))));
        try {
            await moveItems(moved, targetId);
            const target = targetId === UNFILED || !targetId ? "Unfiled" : projects.find((p) => p.id === targetId)?.name || "the project";
            setMessage({ severity: "success", text: `${moved.length} item${moved.length === 1 ? "" : "s"} moved to ${target}.` });
            reloadProjects();
        } catch (error) {
            console.error("Moving the items failed:", error);
            setItems(before);
            setMessage({ severity: "error", text: projectErrorMessage(error, "Moving the items failed.") });
        }
    };

    const dragPropsOf = (kind, row) => {
        const key = keyOf(kind, row.id);
        return {
            draggable: true,
            onDragStart: (event) => {
                const picked = selectedKeys.has(key)
                    ? [...selectedKeys].map((k) => { const [itemKind, id] = k.split(":"); return { kind: itemKind, id }; })
                    : [{ kind, id: row.id }];
                writeDragItems(event.dataTransfer, picked);
            },
            onClick: (event) => {
                // Ctrl/Cmd-click picks the card for a multi-card drag; the
                // buttons inside keep their own click.
                if (!(event.ctrlKey || event.metaKey)) return;
                if (event.target.closest("button, a")) return;
                event.preventDefault();
                setSelectedKeys((old) => {
                    const next = new Set(old);
                    if (next.has(key)) next.delete(key); else next.add(key);
                    return next;
                });
            },
        };
    };

    // ---- projects: create / rename / delete ----------------------------------
    const submitProjectDialog = async () => {
        if (!projectDialog) return;
        const name = projectDialog.name.trim();
        if (!name) { setProjectDialogError("The project needs a name."); return; }
        try {
            if (projectDialog.mode === "create") {
                const created = await createProject(name, projectDialog.description.trim());
                setProjectDialog(null);
                await reloadProjects();
                navigate(projectRoute(created.id));
            } else {
                await updateProject(projectDialog.project.id, { name, description: projectDialog.description.trim() });
                setProjectDialog(null);
                reloadProjects();
            }
        } catch (error) {
            console.error("Saving the project failed:", error);
            setProjectDialogError(projectErrorMessage(error, "Saving the project failed."));
        }
    };

    const confirmDeleteProject = async () => {
        const project = deleteProjectDialog?.project;
        if (!project) return;
        try {
            await deleteProject(project.id);
            setDeleteProjectDialog(null);
            setMessage({ severity: "success", text: `Project “${project.name}” deleted; its items are unfiled.` });
            await reloadProjects();
            navigate(projectRoute(UNFILED), { replace: true });
        } catch (error) {
            console.error("Deleting the project failed:", error);
            setDeleteProjectDialog(null);
            setMessage({ severity: "error", text: projectErrorMessage(error, "Deleting the project failed.") });
        }
    };

    // ---- items: delete / open / download -------------------------------------
    const deleteRule = deleteItemDialog ? DELETE_RULES[deleteItemDialog.kind] : null;
    const deleteExpected = deleteItemDialog ? deleteRule.confirm(deleteItemDialog.row) : "";
    const deleteArmed = Boolean(deleteItemDialog) && deleteExpected.length > 0 && deleteItemDialog.input.trim() === deleteExpected;
    const confirmDeleteItem = async () => {
        if (!deleteItemDialog || !deleteArmed) return;
        const { kind, row } = deleteItemDialog;
        try {
            await axios.delete(DELETE_RULES[kind].endpoint(row.id));
        } catch (error) {
            console.error(`Deleting the ${kind} failed:`, error);
            setMessage({ severity: "warning", text: DELETE_RULES[kind].failed(error) });
        }
        setDeleteItemDialog(null);
        loadItems();
        reloadProjects();
    };

    const handleDownloadSource = async (row) => {
        setDownloadingId(row.id);
        try {
            await downloadReproductionSource(row.id, row.original_file_name);
        } catch (error) {
            console.error("Error downloading the input file:", error);
            setMessage({ severity: "error", text: "Downloading the input file failed." });
        } finally {
            setDownloadingId(null);
        }
    };

    // ---- "New" inside the open project ---------------------------------------
    const newInProject = (route) => navigate(withProjectParam(route, activeProject ? activeProject.id : null));
    const selectionOptions = SELECTION_KIND_KEYS.map((key) => ({
        key, label: SELECTION_KINDS[key].newLabel, route: selectionSetupRoute(SELECTION_KINDS[key].type, "name"),
    }));
    const reproductionOptions = REPRODUCTION_KIND_SLUGS.map((slug) => ({
        key: slug, label: REPRODUCTION_KINDS[slug].newTitle, route: newReproductionRoute(REPRODUCTION_KINDS[slug].type),
    }));

    const counts = activeProject ? countsOf(activeProject) : countsOf(unfiled);
    const title = activeProject ? activeProject.name : "Unfiled";
    const subtitle = activeProject
        ? (activeProject.description || counts.long)
        : "Items that belong to no project. Drag a card onto a project to file it.";

    return (
        <Stack direction="column" alignItems="center" spacing={4} marginTop="2%">
            <Box sx={{ width: "100%", maxWidth: "1400px", px: 2, boxSizing: "border-box" }}>
                <Box
                    sx={{
                        display: "grid",
                        gap: 2.5,
                        gridTemplateColumns: { xs: "1fr", md: "300px minmax(0, 1fr)" },
                        alignItems: "start",
                    }}
                >
                    <ProjectList
                        projects={projects}
                        unfiled={unfiled}
                        activeId={activeId}
                        onSelect={(id) => navigate(projectRoute(id))}
                        onDropItems={moveTo}
                        onNew={() => { setProjectDialogError(null); setProjectDialog({ mode: "create", name: "", description: "" }); }}
                        onRename={(project) => {
                            setProjectDialogError(null);
                            setProjectDialog({ mode: "rename", project, name: project.name, description: project.description || "" });
                        }}
                        onDelete={(project) => setDeleteProjectDialog({ project, input: "" })}
                        deleteRightsOf={(project) => rightsOf(project, "project")}
                    />

                    <Box sx={{ minWidth: 0 }}>
                        {/* header: name, description, toolbar */}
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            alignItems={{ xs: "stretch", sm: "flex-start" }}
                            spacing={2}
                            sx={{ mb: 2.5 }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="h5" sx={{ fontWeight: 700, wordBreak: "break-word" }}>{title}</Typography>
                                <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
                            </Box>
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
                                {selectedKeys.size > 0 ? (
                                    <Chip
                                        size="small"
                                        label={`${selectedKeys.size} selected`}
                                        onDelete={() => setSelectedKeys(new Set())}
                                        color="primary"
                                        variant="outlined"
                                    />
                                ) : null}
                                <ListScopeToggle value={scope} onChange={setScope} />
                            </Stack>
                        </Stack>

                        {message ? (
                            <Alert severity={message.severity} sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setMessage(null)}>
                                {message.text}
                            </Alert>
                        ) : null}

                        {/* The previous cards stay in place, dimmed, while the next
                            project loads: the column never collapses, so nothing
                            jumps while the request is in flight. */}
                        <Box sx={{ position: "relative" }}>
                            {items.loading ? (
                                <LinearProgress
                                    aria-label="Loading items"
                                    sx={{ position: "absolute", top: -8, left: 0, right: 0, height: 3, borderRadius: 999, zIndex: 3 }}
                                />
                            ) : null}
                            <Box
                                sx={{
                                    opacity: items.loading ? 0.45 : 1,
                                    pointerEvents: items.loading ? "none" : "auto",
                                    transition: "opacity 0.2s ease",
                                    // Keeps the page scrollable even for an empty project,
                                    // so the sticky panel and the gutter behave the same everywhere.
                                    minHeight: "60vh",
                                }}
                            >
                                <Section
                                    kind="exploration"
                                    count={items.explorations.length}
                                    action={(
                                        <NewButton kind="exploration" onClick={() => newInProject(EXPLORATION_SETUP_ROUTE)}>
                                            New exploration
                                        </NewButton>
                                    )}
                                >
                                    {items.explorations.map((row) => (
                                        <ExplorationCard
                                            key={row.id}
                                            row={row}
                                            rights={rightsOf(row, "exploration")}
                                            onPrimary={() => navigate(explorationResumeRoute(row.id, normalizeExplorationStatus(row.status)))}
                                            onAnalyse={() => navigate(explorationRoute(row.id, "analyse"))}
                                            onDelete={() => setDeleteItemDialog({ kind: "exploration", row, input: "" })}
                                            projects={projects}
                                            onMoveToProject={(target) => moveTo(target || UNFILED, [{ kind: "exploration", id: row.id }])}
                                            showProject={false}
                                            selected={selectedKeys.has(keyOf("exploration", row.id))}
                                            dragProps={dragPropsOf("exploration", row)}
                                        />
                                    ))}
                                </Section>
                                <Section
                                    kind="selection"
                                    count={items.selections.length}
                                    action={(
                                        <NewMenuButton kind="selection" label="New selection" options={selectionOptions} onChoose={newInProject} />
                                    )}
                                >
                                    {items.selections.map((row) => (
                                        <SelectionCard
                                            key={row.id}
                                            row={row}
                                            rights={rightsOf(row, "selection")}
                                            onContinue={() => navigate(selectionResumeRoute(row.id, row.status, row.selection_type))}
                                            onOverview={() => navigate(selectionRoute(row.selection_type, row.id, "overview"))}
                                            onDelete={() => setDeleteItemDialog({ kind: "selection", row, input: "" })}
                                            projects={projects}
                                            onMoveToProject={(target) => moveTo(target || UNFILED, [{ kind: "selection", id: row.id }])}
                                            showProject={false}
                                            selected={selectedKeys.has(keyOf("selection", row.id))}
                                            dragProps={dragPropsOf("selection", row)}
                                        />
                                    ))}
                                </Section>
                                <Section
                                    kind="reproduction"
                                    count={items.reproductions.length}
                                    action={(
                                        <NewMenuButton kind="reproduction" label="New reproduction" options={reproductionOptions} onChoose={newInProject} />
                                    )}
                                >
                                    {items.reproductions.map((row) => (
                                        <ReproductionCard
                                            key={row.id}
                                            row={row}
                                            rights={rightsOf(row, "reproduction")}
                                            thumbnail={thumbnails[row.id]}
                                            onOpen={() => navigate(reproductionRoute(
                                                row.reproduction_type || "LOGO_REPRODUCTION", row.id, row.setup_final ? "marking" : "setup",
                                            ))}
                                            onDownload={() => handleDownloadSource(row)}
                                            downloading={downloadingId === row.id}
                                            onDelete={() => setDeleteItemDialog({ kind: "reproduction", row, input: "" })}
                                            projects={projects}
                                            onMoveToProject={(target) => moveTo(target || UNFILED, [{ kind: "reproduction", id: row.id }])}
                                            showProject={false}
                                            selected={selectedKeys.has(keyOf("reproduction", row.id))}
                                            dragProps={dragPropsOf("reproduction", row)}
                                        />
                                    ))}
                                </Section>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* New / rename project */}
            <Dialog open={Boolean(projectDialog)} onClose={() => setProjectDialog(null)} fullWidth maxWidth="xs">
                <DialogTitle>{projectDialog?.mode === "rename" ? "Rename project" : "New project"}</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        autoFocus
                        size="small"
                        sx={{ mt: 1 }}
                        label="Name"
                        value={projectDialog?.name || ""}
                        onChange={(event) => setProjectDialog((old) => ({ ...old, name: event.target.value }))}
                        onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); submitProjectDialog(); } }}
                        error={Boolean(projectDialogError)}
                        helperText={projectDialogError || " "}
                    />
                    <TextField
                        fullWidth
                        size="small"
                        sx={{ mt: 1 }}
                        label="Description"
                        value={projectDialog?.description || ""}
                        onChange={(event) => setProjectDialog((old) => ({ ...old, description: event.target.value }))}
                        multiline
                        minRows={2}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setProjectDialog(null)}>Cancel</Button>
                    <Button variant="contained" disableElevation onClick={submitProjectDialog} disabled={!projectDialog?.name?.trim()}>
                        {projectDialog?.mode === "rename" ? "Save" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete project: type its name to arm the button */}
            <Dialog open={Boolean(deleteProjectDialog)} onClose={() => setDeleteProjectDialog(null)}>
                <DialogTitle>Delete project?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This deletes the project “{deleteProjectDialog?.project?.name}”. Its{" "}
                        {deleteProjectDialog ? countsOf(deleteProjectDialog.project).total : 0} item
                        {deleteProjectDialog && countsOf(deleteProjectDialog.project).total === 1 ? "" : "s"} stay and become unfiled.
                    </DialogContentText>
                    {deleteProjectDialog && rightsOf(deleteProjectDialog.project, "project").warning ? (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            {rightsOf(deleteProjectDialog.project, "project").warning}
                        </Alert>
                    ) : null}
                    <TextField
                        fullWidth
                        autoFocus
                        size="small"
                        sx={{ mt: 2 }}
                        label="Type the project name to confirm"
                        placeholder={deleteProjectDialog?.project?.name || ""}
                        value={deleteProjectDialog?.input || ""}
                        onChange={(event) => setDeleteProjectDialog((old) => ({ ...old, input: event.target.value }))}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteProjectDialog(null)}>Cancel</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={confirmDeleteProject}
                        disabled={!deleteProjectDialog || deleteProjectDialog.input.trim() !== deleteProjectDialog.project.name}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete item: the same confirmation as the overview lists */}
            <Dialog open={Boolean(deleteItemDialog)} onClose={() => setDeleteItemDialog(null)}>
                <DialogTitle>{deleteRule?.title}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {deleteItemDialog ? deleteRule.text(deleteItemDialog.row, deleteExpected) : null}
                    </DialogContentText>
                    {deleteItemDialog && rightsOf(deleteItemDialog.row, deleteItemDialog.kind).warning ? (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            {rightsOf(deleteItemDialog.row, deleteItemDialog.kind).warning}
                        </Alert>
                    ) : null}
                    <TextField
                        fullWidth
                        autoFocus
                        size="small"
                        sx={{ mt: 2 }}
                        label={deleteRule?.label || ""}
                        placeholder={deleteExpected}
                        value={deleteItemDialog?.input || ""}
                        onChange={(event) => setDeleteItemDialog((old) => ({ ...old, input: event.target.value }))}
                        error={Boolean(deleteItemDialog?.input) && !deleteArmed}
                        helperText={
                            deleteItemDialog?.input && !deleteArmed
                                ? `Does not match ${deleteExpected}`
                                : `Enter ${deleteExpected} exactly to enable deletion`
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteItemDialog(null)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={confirmDeleteItem} disabled={!deleteArmed}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
